import React, { useRef } from 'react';
import { alpha, Box, Dialog, DialogContent } from '@mui/material';
import DialogHeader from './dialog-header';
import useBreakpoint from '../../hooks/use-breakpoint';
import { Transition, TransitionSlider } from './transition';
import { DialogType } from './types';
import './dialog-pulse.css';

const DialogComponent: React.FC<DialogType> = (props) => {
    const {
        children,
        open,
        fullScreen,
        disable,
        label,
        icon,
        type,
        maxWidth = 'xs',
        width,
        strictClosing,
        isSlider,
        keepMounted = true,
    } = props;

    const { lowerThan } = useBreakpoint();
    const paperRef = useRef<HTMLDivElement>(null);

    const onClose = () => {
        if (disable) return false;
        props?.onClose && props?.onClose();
    };

    const handleClose = (event: object, reason: string) => {
        if (disable) return;

        if (strictClosing && reason === 'backdropClick') {
            if (paperRef.current) {
                paperRef.current.classList.add('pulse');
                setTimeout(() => {
                    paperRef.current?.classList.remove('pulse');
                }, 300);
            }
            return;
        }

        if (strictClosing && reason === 'escapeKeyDown') return;

        props?.onClose && props?.onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={!isSlider ? Transition : TransitionSlider}
            keepMounted={keepMounted}
            slotProps={{
                backdrop: {
                    sx: (theme) => ({
                        backgroundColor: alpha(
                            theme.palette.common.black,
                            theme.palette.mode === 'dark' ? 0.64 : 0.34,
                        ),
                        backdropFilter: 'blur(10px) saturate(115%)',
                        WebkitBackdropFilter: 'blur(10px) saturate(115%)',
                    }),
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    justifyContent: !isSlider ? 'center' : 'flex-end',
                    alignItems: !isSlider ? 'center' : 'stretch',
                },
            }}
            PaperProps={{
                ref: paperRef,
                sx: !isSlider
                    ? {
                          backgroundColor: (theme) =>
                              alpha(theme.palette.background.paper, 0.985),
                          backgroundImage: (theme) =>
                              theme.palette.mode === 'light'
                                  ? `linear-gradient(180deg, ${alpha(
                                        theme.palette.common.white,
                                        0.75,
                                    )} 0%, transparent 42%)`
                                  : `linear-gradient(180deg, ${alpha(
                                        theme.palette.common.white,
                                        0.035,
                                    )} 0%, transparent 38%)`,
                          width: {
                              xs: 'calc(100% - 12px)',
                              sm: fullScreen
                                  ? '100%'
                                  : (width ?? 'fit-content'),
                          },
                          maxWidth: {
                              xs: 'calc(100% - 12px)',
                              sm: 'calc(100% - 48px)',
                          },
                          borderRadius: {
                              xs: '22px 22px 0 0',
                              sm: fullScreen ? '20px 20px 0 0' : '20px',
                          },
                          height: fullScreen ? '100%' : 'fit-content',
                          maxHeight: {
                              xs: 'calc(100dvh - 16px)',
                              sm: 'calc(100dvh - 48px)',
                          },
                          minWidth: { xs: 0, md: width ?? '400px' },
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          bottom: 0,
                          position: {
                              xs: 'absolute',
                              sm: fullScreen ? 'absolute' : 'relative',
                          },
                      }
                    : {
                          height: '100%',
                          width: {
                              xs: '100vw',
                              sm: width ?? '360px',
                          },
                          maxWidth: '100vw',
                          borderRadius: {
                              xs: 0,
                              sm: '20px 0 0 20px',
                          },
                      },
            }}
            fullScreen={
                lowerThan('xs') && fullScreen === undefined
                    ? true
                    : fullScreen || false || !!isSlider
            }
            fullWidth
            maxWidth={maxWidth}
        >
            <DialogHeader
                type={type}
                onClose={onClose}
                label={label}
                icon={icon}
            />

            <DialogContent
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: !isSlider ? 'safe center' : 'flex-start',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    scrollbarGutter: 'stable',
                    backgroundColor: 'transparent',
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 2, sm: 2.5 },
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'divider',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: maxWidthConverter(maxWidth),
                        minWidth: 0,
                    }}
                >
                    {children}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const maxWidthConverter = (maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl') => {
    switch (maxWidth) {
        case 'xs':
            return '600px';
        case 'sm':
            return '900px';
        case 'md':
            return '1200px';
        case 'lg':
        case 'xl':
            return '1536px';
        default:
            return '600px';
    }
};

export default DialogComponent;
export type { DialogType };
